"use strict";

import dispatcher from "./../../dispatcher";
import vehicleHeatmapStore from "./../../store/vehicleHeatmaps";
import displayedDetailsStore from "./../../store/displayedDetails";
import openedDetailsStore from "./../../store/openedDetails";

import vehicleHeatmapUtils from "./../../utils/vehicleHeatmap";
import {coordinateToLatLng} from "./../../utils/geo";
import {pointToLatLng} from "./../../utils/geo";

import grayscale from "./styles/grayscale";
import zIndex from "./zIndex";

const HEATMAP_POLY_OPTIONS = {
	strokeColor: "red",
	strokeWeight: 5,
	geodesic: true,
	zIndex: zIndex.ROUTE_DEFAULT
};

const HEATMAP_START_END_MARKER_ICON = {
	path: google.maps.SymbolPath.CIRCLE,
	scale: 4,
	fillColor: "red",
	strokeOpacity: 0
};

export default class VehicleHeatmapController{
	
	constructor(map){
		this.map = map;
		this.setStoreListeners();
		this.initHeatmaps();
	}
	
	initHeatmaps(){
		this.heatmaps = this.heatmaps || {};
	}
	
	setStoreListeners(){
		vehicleHeatmapStore.on("insert", heatmaps => this.addHeatmaps(heatmaps));
		vehicleHeatmapStore.on("remove", heatmaps => this.removeHeatmaps(heatmaps));
		vehicleHeatmapStore.on("update", heatmap => this.updateHeatmap(heatmap));
		displayedDetailsStore.on("change", _ => this.updateHeatmaps());
		openedDetailsStore.on("change", _ => this.updateHeatmaps());
	}

	addHeatmaps(heatmaps){
		heatmaps.forEach(heatmap => this.addHeatmap(heatmap));
	}
	
	removeHeatmaps(heatmaps){
		heatmaps.forEach(heatmap => this.removeHeatmap(heatmap.vehicle));
	}
	
	addHeatmap(heatmap){
		const vehicleId = heatmap.vehicle;
		this.removeHeatmap(vehicleId);
		
		const opacity = vehicleHeatmapUtils.getHeatmapShapeOpacity(vehicleId);
		const shapes = heatmap.sections.map(section => this.createShapeCollection(section, opacity));
		
		this.heatmaps[vehicleId] = {shapes, sections: heatmap.sections};
		
		this.updateVisibility(vehicleId);
		
		if( vehicleHeatmapUtils.getVisibilityOfHeatmap(vehicleId) ) {
			this.focusOnHeatmap(vehicleId);
		}
	}
	
	removeHeatmap(vehicleId){
		const heatmap = this.heatmaps[vehicleId];
		if(!heatmap){
			return;
		}
		
		heatmap.shapes.forEach(shapes => {
			shapes.polyline.setMap(null);
			shapes.startMarker.setMap(null);
			shapes.endMarker.setMap(null);
		});
		
		delete this.heatmaps[vehicleId];
	}
	
	updateHeatmap(heatmap){
		if(heatmap.sections != this.heatmaps[heatmap.vehicle].sections){
			this.removeHeatmap(heatmap.vehicle);
			this.addHeatmap(heatmap);
		}
		else{
			this.updateVisibility(heatmap.vehicle);
		}
	}
	
	updateHeatmaps() {
		this.updateMapStyle();
		this.updateVisibilities();
	}
	
	updateMapStyle() {
		const displayedDetails = displayedDetailsStore.get();
		if (!displayedDetails) {
			this.map.setOptions({styles: null}); //default style
			return;
		}
		const detailsDescriptor = openedDetailsStore.get(displayedDetails.id);
		if (detailsDescriptor && detailsDescriptor.activePage == "heatmap") {
			this.map.setOptions({styles: grayscale});
		}
		else {
			this.map.setOptions({styles: null}); //default style
		}
	}
	
	updateVisibilities() {	
		for (let vehicleId in this.heatmaps) {
			this.updateVisibility(vehicleId);
		}
	}
	
	updateVisibility(vehicleId){
		const heatmapShapes = this.heatmaps[vehicleId].shapes;
		
		const canBeDisplayed = vehicleHeatmapUtils.getVisibilityOfHeatmap(vehicleId);
		const descriptor = vehicleHeatmapStore.get(vehicleId);
		
		heatmapShapes.forEach(
			shape => {
				shape.startMarker.setVisible(canBeDisplayed && descriptor && descriptor.show.stops);
				shape.endMarker.setVisible(canBeDisplayed && descriptor && descriptor.show.stops);
				shape.polyline.setVisible(canBeDisplayed && descriptor && descriptor.show.routes);
			}
		);
	}
	
	focusOnHeatmap(vehicleId){
		const heatmap = vehicleHeatmapStore.get(vehicleId);
		
		if(heatmap.sections.length == 0){
			return;
		}
		
		const bounds = new google.maps.LatLngBounds();
		
		heatmap.sections.forEach(
			section => section.coordinates.forEach(
				coordinate => {
					const point = coordinateToLatLng(coordinate);
					bounds.extend(point);
				}
			)
		);
		
		dispatcher.dispatch("setMap", {bounds: bounds.toJSON()});
	}
	
	createShapeCollection(section, opacity) {
		const path = section.coordinates.map(coordinateToLatLng);
		const startPosition = coordinateToLatLng(section.coordinates[0]);
		const endPosition =  coordinateToLatLng(section.coordinates[section.coordinates.length-1]);
		return {
			polyline:		this.createHeatmapPolyline(path, opacity),
			startMarker:	this.createHeatmapStartEndMarker(startPosition, opacity),
			endMarker:		this.createHeatmapStartEndMarker(endPosition, opacity)
		};
	}
	
	createHeatmapStartEndMarker(position, opacity){
		const marker = new google.maps.Marker({
			map: this.map,
			position: position
		});
		
		let icon = HEATMAP_START_END_MARKER_ICON;
		icon["fillOpacity"] = opacity;
		
		marker.setOptions({
			zIndex: zIndex.ROUTE_START_END_MARKER,
			icon: icon
		});
		return marker;
	}
	
	createHeatmapPolyline(path, opacity){
		const polyline = new google.maps.Polyline({
			path:	path,
			map:	this.map
		});
		
		let polyOptions = HEATMAP_POLY_OPTIONS;
		polyOptions["strokeOpacity"] = opacity;
		
		polyline.setOptions(polyOptions);
		return polyline;
	}
	
}


// WEBPACK FOOTER //
// ./src/view/Map/VehicleHeatmapController.js